import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { fetchFacetSuggestions } from "../services/facetService";
import { FacetKey, FacetToken } from "../types/facets";
import { clamp } from "../utils/clamp";

const ALIASES: Record<string, FacetKey> = {
  pilot: "pilot",
  p: "pilot",
  actor: "actor",
  a: "actor",
  unit: "unit",
  u: "unit",
  weapon: "weapon",
  w: "weapon",
  airdrome: "airdrome",
  air: "airdrome",
  airfield: "airdrome",
  server: "server",
  s: "server",
  event: "event",
  e: "event",
  kind: "kind",
  k: "kind"
};

const DISPLAY_KEY: Record<FacetKey, string> = {
  pilot: "pilot",
  pilot_uid: "pilot",
  actor: "actor",
  unit: "unit",
  weapon: "weapon",
  airdrome: "airdrome",
  server: "server",
  event: "event",
  kind: "kind"
};

interface FacetInputProps {
  value: FacetToken[];
  onChange: (tokens: FacetToken[]) => void;
  onClear?: () => void;
  placeholder?: string;
}

export default function FacetInput({ value, onChange, onClear, placeholder }: FacetInputProps) {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const requestSeq = useRef(0);

  const parsed = useMemo(() => parseFacetDraft(input), [input]);

  useEffect(() => {
    let active = true;

    async function run() {
      const draft = parsed;
      if (!draft || !draft.key) {
        setSuggestions([]);
        setSuggestionOpen(false);
        setError(null);
        return;
      }

      // Only autocomplete once we have `key:`
      if (!draft.hasColon) {
        setSuggestions([]);
        setSuggestionOpen(false);
        setError(null);
        return;
      }

      // For kind, local suggestions.
      if (draft.key === "kind") {
        const opts = ["event", "proficiency", "achievement"].filter((k) => k.includes(draft.value.toLowerCase()));
        setSuggestions(opts);
        setSuggestionOpen(true);
        setHighlightIndex(0);
        setError(null);
        return;
      }

      // actor is UID-based; no autocomplete endpoint yet.
      if (draft.key === "actor") {
        setSuggestions([]);
        setSuggestionOpen(false);
        setError(null);
        return;
      }

      const seq = ++requestSeq.current;
      setLoading(true);
      setError(null);

      try {
        const q = stripTrailingWildcard(draft.value);
        const values = await fetchFacetSuggestions(draft.key, q);
        if (!active || seq !== requestSeq.current) return;
        setSuggestions(values);
        setSuggestionOpen(true);
        setHighlightIndex(0);
      } catch (err) {
        if (!active || seq !== requestSeq.current) return;
        const message = err instanceof Error ? err.message : "Unexpected error";
        setSuggestions([]);
        setSuggestionOpen(false);
        setError(message);
      } finally {
        if (!active || seq !== requestSeq.current) return;
        setLoading(false);
      }
    }

    // tiny debounce
    const id = window.setTimeout(() => void run(), 120);
    return () => {
      active = false;
      window.clearTimeout(id);
    };
  }, [parsed]);

  const addToken = (token: FacetToken) => {
    const next = mergeToken(value, token);
    onChange(next);
    setInput("");
    setSuggestions([]);
    setSuggestionOpen(false);
    setHighlightIndex(0);
    setError(null);
    requestSeq.current += 1;
  };

  const removeTokenAt = (index: number) => {
    const next = value.slice(0, index).concat(value.slice(index + 1));
    onChange(next);
    inputRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && input.length === 0 && value.length > 0) {
      e.preventDefault();
      removeTokenAt(value.length - 1);
      return;
    }

    if (e.key === "Escape") {
      setSuggestionOpen(false);
      return;
    }

    if (suggestionOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      const delta = e.key === "ArrowDown" ? 1 : -1;
      const max = Math.max(suggestions.length - 1, 0);
      setHighlightIndex((prev) => clamp(prev + delta, 0, max));
      return;
    }

    if (e.key === "Enter" || e.key === "Tab") {
      const draft = parseFacetDraft(input);
      if (!draft || !draft.key || !draft.hasColon) {
        return;
      }

      const draftHasWildcard = hasTrailingWildcard(draft.value);
      const picked = !draftHasWildcard && suggestionOpen && suggestions.length > 0 ? suggestions[highlightIndex] : null;
      const tokenValue = normalizeFacetValue(picked ?? draft.value);
      if (!tokenValue) {
        return;
      }

      e.preventDefault();
      addToken({ key: draft.key, value: tokenValue });
    }
  };

  return (
    <div className="facet-input" onMouseDown={() => inputRef.current?.focus()}>
      <div className="facet-input-inner">
        <span className="facet-input-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2"/>
            <path d="M16.5 16.5 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </span>

        <div className="facet-pills" role="list" aria-label="Active filters">
          {value.map((token, idx) => (
            <button
              key={`${token.key}:${token.value}:${idx}`}
              type="button"
              className="facet-pill"
              onClick={(e) => {
                e.stopPropagation();
                removeTokenAt(idx);
              }}
              role="listitem"
              title="Remove filter"
            >
              <span className="facet-pill-key">{DISPLAY_KEY[token.key]}:</span>
              <span className="facet-pill-value">{token.label ?? token.value}</span>
              <span className="facet-pill-x" aria-hidden="true">×</span>
            </button>
          ))}

          <input
            ref={inputRef}
            className="facet-input-field"
            name="facet-input"
            id="feed-filter-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={
              value.length === 0
                ? (placeholder ?? "Filter: pilot:\"Maverick\" unit:F-14A (* matches partly)")
                : undefined
            }
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>

        <div className="facet-input-actions" aria-hidden={!(loading || (onClear && value.length > 0))}>
          {loading && <span className="facet-loading">…</span>}

          {onClear && value.length > 0 && (
            <button
              type="button"
              className="facet-clear"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
                inputRef.current?.focus();
              }}
              aria-label="Clear all filters"
              title="Clear all"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {error && <div className="facet-error">{error}</div>}

      {suggestionOpen && suggestions.length > 0 && parsed?.key && (
        <div className="facet-suggestions" role="listbox" aria-label="Suggestions">
          {suggestions.map((s, idx) => (
            <button
              key={`${s}:${idx}`}
              type="button"
              className={"facet-suggestion" + (idx === highlightIndex ? " is-active" : "")}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                addToken({ key: parsed.key!, value: normalizeFacetValue(s) });
              }}
              role="option"
              aria-selected={idx === highlightIndex}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function mergeToken(tokens: FacetToken[], next: FacetToken): FacetToken[] {
  // One value per key for now; replace existing
  const filtered = tokens.filter((t) => t.key !== next.key);
  return [...filtered, next];
}

function parseFacetDraft(text: string): { key: FacetKey | null; value: string; hasColon: boolean } | null {
  const raw = text.trim();
  if (!raw) return null;

  const colonIndex = raw.indexOf(":");
  if (colonIndex < 0) {
    const maybeKey = normalizeFacetKey(raw);
    return { key: maybeKey, value: "", hasColon: false };
  }

  const keyPart = raw.slice(0, colonIndex).trim();
  const valuePart = raw.slice(colonIndex + 1);
  const key = normalizeFacetKey(keyPart);
  return { key, value: valuePart.trimStart(), hasColon: true };
}

function normalizeFacetKey(input: string): FacetKey | null {
  const key = input.toLowerCase().trim();
  return ALIASES[key] ?? null;
}

function normalizeFacetValue(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "";

  // Allow pilot:"Test Pilot 1"
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1).trim();
  }

  // Allow pilot:(Test Pilot 1)
  if (trimmed.startsWith("(") && trimmed.endsWith(")")) {
    return trimmed.slice(1, -1).trim();
  }

  return trimmed;
}


function stripTrailingWildcard(input: string): string {
  const trimmed = input.trimStart();
  if (trimmed === "*") return "";
  if (trimmed.endsWith("*")) {
    return trimmed.slice(0, -1).trimEnd();
  }
  return trimmed;
}

function hasTrailingWildcard(input: string): boolean {
  const trimmed = input.trim();
  return trimmed.length > 1 && trimmed.endsWith("*");
}
